import React, { FC, useEffect, useMemo, useState } from "react";
import { Table, Input, Tooltip, Pagination } from "antd";
import { Button } from "reactstrap";
import { ColumnsType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";

import { Icon } from "../../../components/Component";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import purchaseRequestApis from "../../../api/master/purchaseRequest";

interface TrackIndentListProps {
  projectId: string | number | undefined;
  projectName?: string;
  siteId?: string | number | undefined;
}

interface IndentRow {
  prId: number;
  prSequence: string;
  referenceIndentNumber?: string;
  indentDate?: string;
  siteName?: string;
  requestByName?: string;
  lastDateOfSubmission?: string;
  deliveryAddressName?: string;
  createdUser?: string;
  createdTime?: string;
  actualStatus?: string;
}

const statusColor = (text?: string) => {
  switch (text) {
    case "Approved":
      return "#28A745";
    case "Open":
      return "#007BFF";
    case "In Progress":
      return "#FD7E14";
    case "Rejected":
      return "#DC3545";
    case "Pending":
      return "#FFC107";
    default:
      return "#262626";
  }
};

const formatDate = (text?: string) =>
  text ? dayjs(text).format("DD-MMM-YYYY") : "";

const TrackIndentList: FC<TrackIndentListProps> = ({
  projectId,
  projectName,
  siteId,
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState<IndentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchList = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    search = searchQuery
  ) => {
    if (!siteId) return;
    try {
      setLoading(true);
      const params: any = {
        sortOrder: "DESC",
        pageSize,
        direction: "P",
        pageDiff: page - 1,
        paginationId: null,
        searchQuery: search || undefined,
        siteIds: String(siteId),
        prSource: "TASK",
      };
      const response: any = await purchaseRequestApis.fetchPrListPagination(
        params
      );
      const { data: respData, status, message } = response || {};
      if (status) {
        const payload = respData || response?.response?.data || response?.response;
        const list = payload?.list || payload || [];
        const count = payload?.count ?? list.length;
        setData(list);
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize,
          total: Number(count) || 0,
        }));
      } else if (message) {
        Swal.fire("Error", message, "error");
      }
    } catch (err) {
      console.error("Error fetching indent list:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1, pagination.pageSize, searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const onSearch = (value: string) => {
    setSearchQuery(value);
    fetchList(1, pagination.pageSize, value);
  };

  const columns: ColumnsType<IndentRow> = useMemo(
    () => [
      {
        title: "PR #",
        dataIndex: "prSequence",
        key: "prSequence",
        fixed: "left",
        width: 200,
      },
      {
        title: "Indent Number",
        dataIndex: "referenceIndentNumber",
        key: "referenceIndentNumber",
        width: 180,
      },
      {
        title: "Indent Date",
        dataIndex: "indentDate",
        key: "indentDate",
        width: 140,
        render: (text: string) => <span>{formatDate(text)}</span>,
      },
      {
        title: "Site",
        dataIndex: "siteName",
        key: "siteName",
        width: 200,
      },
      {
        title: "Requested By",
        dataIndex: "requestByName",
        key: "requestByName",
        width: 150,
      },
      {
        title: "Need By Date",
        dataIndex: "lastDateOfSubmission",
        key: "lastDateOfSubmission",
        width: 140,
        render: (text: string) => <span>{formatDate(text)}</span>,
      },
      {
        title: "Delivery Location",
        dataIndex: "deliveryAddressName",
        key: "deliveryAddressName",
        width: 180,
      },
      {
        title: "Created By",
        dataIndex: "createdUser",
        key: "createdUser",
        width: 140,
      },
      {
        title: "Created Date",
        dataIndex: "createdTime",
        key: "createdTime",
        width: 140,
        render: (text: string) => <span>{formatDate(text)}</span>,
      },
      {
        title: "Status",
        dataIndex: "actualStatus",
        key: "actualStatus",
        fixed: "right",
        width: 130,
        render: (text: string) => (
          <span style={{ color: statusColor(text), fontWeight: 600 }}>
            {text}
          </span>
        ),
      },
      {
        title: "Action",
        key: "operation",
        fixed: "right",
        width: 110,
        render: (_: any, record: IndentRow) => (
          <div style={{ display: "flex", gap: 6 }}>
            <Tooltip title="View">
              <Button
                size="sm"
                color="light"
                onClick={() =>
                  navigate(`/pms/project/view-pr/${record.prId}`, {
                    state: {
                      prNumber: record.prSequence,
                      indentNumber: record.referenceIndentNumber,
                      projectId,
                      projectName,
                    },
                  })
                }
              >
                <Icon name="focus" />
              </Button>
            </Tooltip>
            <Tooltip title="View Tracking">
              <Button
                size="sm"
                color="light"
                onClick={() =>
                  navigate(
                    `/pms/project/track-purchase-requests/${record.prId}`,
                    {
                      state: {
                        prNumber: record.prSequence,
                        indentNumber: record.referenceIndentNumber,
                        projectId,
                        projectName,
                      },
                    }
                  )
                }
              >
                <Icon name="activity-round-fill" />
              </Button>
            </Tooltip>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, projectName]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 0 12px 0",
          flexShrink: 0,
        }}
      >
        <div style={{ width: "45%", maxWidth: 420 }}>
          <Input
            placeholder="Search indents..."
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onPressEnter={(e) =>
              onSearch((e.target as HTMLInputElement).value)
            }
            allowClear
            onClear={() => onSearch("")}
            style={{ borderRadius: 6, height: 36 }}
          />
        </div>
        <Button
          color="light"
          size="sm"
          onClick={() => fetchList(pagination.current, pagination.pageSize)}
          title="Refresh"
        >
          <Icon name="redo" />
        </Button>
      </div>

      <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        <SkeletonTable
          loading={loading}
          columns={columns as SkeletonTableColumnsType[]}
        >
          <Table
            className="customTable"
            columns={columns}
            dataSource={data}
            rowKey={(record) => String(record.prId)}
            pagination={false}
            bordered
            size="small"
            scroll={{ x: 1400 }}
          />
        </SkeletonTable>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "12px 0 0 0",
          flexShrink: 0,
        }}
      >
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          onChange={(page, pageSize) => fetchList(page, pageSize)}
          showTotal={(total) => `Total ${total} indents`}
        />
      </div>
    </div>
  );
};

export default TrackIndentList;
